import React from "react";
import { Box, Typography, Button } from "@mui/material";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";

/**
 * Top-level React error boundary. Catches render errors in the component tree
 * and routes them through the analytics façade (PostHog captureException in
 * staging/prod, console adapter in dev).
 *
 * Non-React errors (uncaught exceptions, unhandled promise rejections, and
 * console.error calls) are captured by PostHog's native exception autocapture,
 * configured in src/analytics/adapters/posthog.js. It registers at init() —
 * before this boundary mounts — so it also covers startup failures.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    Analytics.captureError(error, {
      component_stack: errorInfo?.componentStack,
      route: window.location.pathname,
    });
    Analytics.track(EVENTS.ERROR_OCCURRED, {
      error_message: error?.message,
      error_name: error?.name,
      component_stack: errorInfo?.componentStack,
      route: window.location.pathname,
      severity: "fatal",
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          p={4}
          textAlign="center"
        >
          <Typography variant="h4" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            An unexpected error occurred. Try refreshing the page.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Refresh Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
